import { ContentBlockShape, RichText } from "@/models";
import { Editor, EditorContent } from "@tiptap/react";
import React, { useCallback, useMemo } from "react";
import { useDebouncedEditor } from "@/modules/text-editor/useDebouncedEditor";
import { RichTextTooltipMenu } from "@/components/rich-text";
import { useContentBlockState } from "../../Design/useContentBlockState";
import { cn } from "@/util";
import { themeToStyleVariables } from "@/tailwind-colors/themePropertyToStyleVariable";
import { useUpdateOption } from "../../Design/content-block-management/useSaveOption";
import { useGetElementAsync } from "@/hooks/useGetElementAsync";

import { useSaveRichText } from "../../Design/content-block-management/useSaveRichText";
import { useGotContentBlock } from "@/hooks";
import {
    useGetActiveContentBlock,
    useSelectActiveContentBlock,
} from "@/hooks/activeContentBlock";

function styleModelQuantity(editor: Editor, values: Record<string, string>) {
    const elements = Array.from(
        editor.view.dom.querySelectorAll("[data-model-variable-id]"),
    ) as HTMLElement[];
    console.log("styleModelQuantity", elements, editor, values);

    for (const el of elements) {
        el.classList.add("animate-pulse");
        el.classList.add("loading-model-quantity");
        const key = !!el.getAttribute("data-time-horizon-id")
            ? el.getAttribute("data-model-variable-id") +
              "_" +
              el.getAttribute("data-time-horizon-id")
            : el.getAttribute("data-model-variable-id");

        if (!el.isConnected || !values?.[key]) {
            console.log("no model value", key, el.isConnected, values);
            continue;
        }
        // el.style.setProperty("--modal-quantity-text", `"${values?.[key]}"`);
        el.classList.remove("animate-pulse");
        el.classList.remove("loading-model-quantity");
    }
}

type Props = {
    editable: boolean;
    content: RichText;
    contentBlock: ContentBlockShape;
    saveOverride?: (rich_text: RichText | App.Data.RichTextData) => void;
    delayMilliseconds?: number;
};

const isTextSelected = (editor: Editor) => {
    if (!editor) return false;
    const { from, to } = editor.state.selection;
    return from !== to;
};

const RichTextBlock = React.memo(function RichTextBlock({
    editable = false,
    content,
    contentBlock,
    saveOverride,
    delayMilliseconds = 500,
}: Props) {
    const { mutateAsync } = useSaveRichText();
    const { gotContentBlock } = useGotContentBlock();
    const { setContentBlock } = useContentBlockState();
    const { mutateAsync: updateOption } = useUpdateOption();
    const { setActiveContentBlock } = useSelectActiveContentBlock();
    const { data: activeContentBlock } = useGetActiveContentBlock();

    const handleSave = useCallback(
        (rich_text: RichText | App.Data.RichTextData, text?: string) => {
            gotContentBlock({
                ...contentBlock,
                rich_text: rich_text as RichText,
            });
            mutateAsync({
                content_block_id: contentBlock.id,
                rich_text: rich_text as RichText,
            });
        },
        [contentBlock?.id, editable],
    );

    const editor = useDebouncedEditor({
        richText: content,
        isEditable: editable,
        updateOverride: saveOverride || handleSave,
        editedId: undefined,
        waitMilliseconds: delayMilliseconds,
        placeholder:
            editable && (!content || !Object.keys(content)?.length)
                ? "Add some text..."
                : "",
        onBlur: async (rich_text) => {
            // setContentBlock({
            //     ...contentBlock,
            //     rich_text: rich_text as RichText,
            // });
            mutateAsync({
                content_block_id: contentBlock.id,
                rich_text: rich_text as RichText,
            });
            if (contentBlock.content_block_type === "OPTION") {
                updateOption({
                    content: editor.getText(),
                    content_block_id: contentBlock.id,
                });
            }
        },
    });

    const styleVariables =
        contentBlock.content_block_type === "RICH_TEXT"
            ? themeToStyleVariables(contentBlock.theme)
            : {};

    const element = useGetElementAsync(
        `[data-content-block-id="${contentBlock.id}"]`,
    );

    const showMenuInBubble = useMemo(() => {
        return element?.getBoundingClientRect().width > 400;
    }, [element]);

    const isElearning = !!useGetElementAsync(".e-learning");

    // Import-mode rich text (tailwindClasses present) needs a block-level
    // wrapper. The default <span> is inline — its background, border, and
    // padding only paint behind the rendered text run, which is why
    // imported callouts like .slider-outlook used to render with their
    // background visible as a narrow strip on the left instead of filling
    // the parent's width. A <div> takes the parent's full width and the
    // source CSS's bg/padding/border rules paint as the designer intended.
    const isImportMode =
        contentBlock.content_block_type === "RICH_TEXT" &&
        !!contentBlock.theme?.tailwindClasses?.length;
    const Wrapper = (isImportMode ? "div" : "span") as
        | "div"
        | "span";

    return (
        <Wrapper
            data-content-block-id={contentBlock.id}
            data-parent-content-block-id={
                contentBlock.content_block_type === "RICH_TEXT"
                    ? contentBlock.parent_content_block_id
                    : undefined
            }
            className={`relative ${cn(
                contentBlock.content_block_type === "RICH_TEXT"
                    ? contentBlock.theme?.tailwindClasses || []
                    : [],
            )} xs:max-w-2xl group
            data-[is-elearning=true]:w-full data-[is-option=true]:w-full sm:max-w-3xl
            md:max-w-4xl lg:max-w-5xl xl:max-w-6xl`}
            style={styleVariables}
            data-is-option={contentBlock.content_block_type === "OPTION"}
            data-is-elearning={isElearning}
            onClick={() => {
                // Card-option editors render inline and never pass through a
                // SelectContentBlockButton, so clicking their text doesn't
                // select the option (and leaves any prior selection stale).
                // Claim selection here so editing an option's text makes the
                // option the active block. onClick is the last event in the
                // press sequence (after mousedown/focus/mouseup), so it wins
                // even if something earlier changed the active block. Don't
                // clear it onBlur — clicking a bubble-menu button blurs the
                // editor, and clearing would yank the formatting menu away.
                if (
                    (isElearning ||
                        contentBlock.content_block_type === "OPTION") &&
                    activeContentBlock?.id !== contentBlock.id &&
                    editable
                ) {
                    setActiveContentBlock(contentBlock.id);
                }
            }}
            onBlur={() => {
                if (
                    isElearning &&
                    activeContentBlock?.id !== contentBlock.id &&
                    editable
                ) {
                    setActiveContentBlock(undefined);
                }
            }}
        >
            {!!editor && editable && (
                <RichTextTooltipMenu
                    editor={editor}
                    showColorPicker={!!contentBlock.rich_text?.content?.[0]?.content?.[0]?.marks?.[0]?.type}
                    isOpen={true}
                    contentBlockId={contentBlock.id}
                />
            )}

            {/* {!!editor &&
                editable &&
                !showMenuInBubble &&
                isTextSelected(editor) &&
                createPortal(
                    <div
                        className="tippy left-nav-popover-tiptap fixed left-2 top-2 z-[99999999]
                            data-[is-open=false]:opacity-0"
                        data-is-open={isTextSelected(editor)}
                    >
                        <Menu
                            editor={editor}
                            showColorPicker={true}
                            contentBlockId={contentBlock.id}
                        />
                    </div>,
                    document.body,
                )} */}

            <EditorContent
                data-testid="editor-content"
                editor={editor}
                className="min-h-2 w-full min-w-4"
                placeholder="Add some text"
            />
        </Wrapper>
    );
});

export default RichTextBlock;
